import React from 'react';
import { Tooltip } from 'antd';
import { F90H } from '../../../services/p930hApi';
import { Mode } from '../hooks/useP930HMode';

interface P930HPermissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: F90H | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: P930HPermissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const ActionBar: React.FC<ActionBarProps> = React.memo(({
  mode, isEditing, record, currentIdx, allIdsLength, permissions,
  onAdd, onModify, onDelete, onNavTo, onExit,
}) => {
  return (
    <div className="action-bar">
      {/* Navigation */}
      <div className="action-group">
        <button className="btn" disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)} title="第一筆">⏮</button>
        <button className="btn" disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)} title="上一筆">◀</button>
        <span style={{ fontSize: 12, color: '#999', minWidth: 60, textAlign: 'center' }}>
          {allIdsLength > 0 ? `${currentIdx + 1} / ${allIdsLength}` : '0 / 0'}
        </span>
        <button className="btn" disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)} title="下一筆">▶</button>
        <button className="btn" disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)} title="最後一筆">⏭</button>
      </div>

      <div className="action-separator" />

      {/* CRUD Actions */}
      <div className="action-group">
        <PermBtn
          allowed={permissions.canCreate}
          disabled={false}
          className={`btn ${mode === 'add' ? 'btn-primary' : ''}`}
          onClick={onAdd}
        >
          {isEditing ? '💾 儲存' : '➕ 新增'}
        </PermBtn>
        <PermBtn
          allowed={permissions.canEdit}
          disabled={!record && !isEditing}
          className={`btn ${mode === 'edit' ? 'btn-primary' : ''}`}
          onClick={onModify}
        >
          {isEditing ? '↩ 取消' : '✏️ 修改'}
        </PermBtn>
        <PermBtn
          allowed={permissions.canDelete}
          disabled={!record || isEditing}
          className="btn btn-danger"
          onClick={onDelete}
        >
          🗑 刪除
        </PermBtn>
      </div>

      <div className="action-separator" />

      {/* Exit */}
      <div className="action-group">
        <button className="btn" onClick={onExit}>✖ 離開</button>
      </div>
    </div>
  );
});

ActionBar.displayName = 'ActionBar';
export default ActionBar;
